import { useEffect, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { VesselNominationOutletContext } from "@features/vessel-nomination/add/constants";
import { FORM_FIELDS } from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const ArrivalDraught = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();
  const { trigger, watch } = formData;

  const arrivalFwd = watch(FORM_FIELDS.ARRIVAL_FWD);
  const arrivalMid = watch(FORM_FIELDS.ARRIVAL_MID);
  const arrivalAft = watch(FORM_FIELDS.ARRIVAL_AFT);
  const previousValuesRef = useRef({
    arrivalFwd,
    arrivalMid,
    arrivalAft,
  });

  useEffect(() => {
    const previousValues = previousValuesRef.current;

    if (arrivalFwd !== previousValues.arrivalFwd) {
      if (arrivalMid) void trigger(FORM_FIELDS.ARRIVAL_MID);
      if (arrivalAft) void trigger(FORM_FIELDS.ARRIVAL_AFT);
    }

    if (arrivalMid !== previousValues.arrivalMid) {
      if (arrivalFwd) void trigger(FORM_FIELDS.ARRIVAL_FWD);
      if (arrivalAft) void trigger(FORM_FIELDS.ARRIVAL_AFT);
    }

    if (arrivalAft !== previousValues.arrivalAft) {
      if (arrivalFwd) void trigger(FORM_FIELDS.ARRIVAL_FWD);
      if (arrivalMid) void trigger(FORM_FIELDS.ARRIVAL_MID);
    }

    previousValuesRef.current = { arrivalFwd, arrivalMid, arrivalAft };
  }, [arrivalFwd, arrivalMid, arrivalAft, trigger]);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.arrival-draught`)}
        className="grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"
      >
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.ARRIVAL_FWD>
          testId="nomination-arrival-fwd-input"
          name={FORM_FIELDS.ARRIVAL_FWD}
          type="number"
          label={t(`${LANG_BASE}.fields.fwd`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.ARRIVAL_MID>
          testId="nomination-arrival-mid-input"
          name={FORM_FIELDS.ARRIVAL_MID}
          type="number"
          label={t(`${LANG_BASE}.fields.mid`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.ARRIVAL_AFT>
          testId="nomination-arrival-aft-input"
          name={FORM_FIELDS.ARRIVAL_AFT}
          type="number"
          label={t(`${LANG_BASE}.fields.aft`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MAX_TRIM>
          testId="nomination-max-trim-input"
          name={FORM_FIELDS.MAX_TRIM}
          type="number"
          label={t(`${LANG_BASE}.fields.max-trim`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
      </FormSection>
    </FormProvider>
  );
};

export default ArrivalDraught;
